/**
 * Formatting helpers — money, dates, names.
 */
import { format, formatDistanceToNow, parseISO } from 'date-fns';

export function formatMoneyCents(cents: number | null | undefined): string {
  if (cents == null) return '—';
  const dollars = cents / 100;
  return dollars.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

/**
 * Format a *date-only* value — one that carries no meaningful time-of-day
 * (requested_start_date, requested_end_date, deadline_date). The API stores
 * these as UTC-midnight timestamps ("2026-06-29T00:00:00.000Z"), so rendering
 * the instant in local time rolls it back a day for any timezone behind UTC
 * (every US zone) — the classic off-by-one. Read the calendar date straight
 * from the ISO string instead, so it shows the day that was actually picked.
 * Also accepts a bare "YYYY-MM-DD".
 */
export function formatDateOnly(value: string | null | undefined): string {
  if (!value) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (m) {
    const [, y, mo, d] = m;
    // Construct a *local* date from the parts — no UTC conversion, no shift.
    return format(new Date(Number(y), Number(mo) - 1, Number(d)), 'MMM d, yyyy');
  }
  return formatDate(value);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'MMM d · h:mm a');
  } catch {
    return iso;
  }
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}
